import { useState, useEffect, useRef, useCallback } from 'react';
import { searchAdminActors } from '../../../api/adminApi';

const SEARCH_DEBOUNCE_MS = 300;
const RESULT_LIMIT = 8;

/** 동명이인을 가르는 데 쓰는 보조 정보 — 있는 것만 이어 붙인다 */
export function describeActor(actor) {
  if (!actor) return '';
  const gender = actor.gender?.toUpperCase() === 'MALE' ? '남'
    : actor.gender?.toUpperCase() === 'FEMALE' ? '여'
      : null;
  const birth = actor.birthYear ? `${actor.birthYear}년생`
    : actor.birthDate ? `${String(actor.birthDate).slice(0, 4)}년생`
      : null;
  const agency = actor.agency ?? actor.agencyName ?? null;
  return [birth, gender, agency].filter(Boolean).join(' · ');
}

/**
 * 가입한 배우를 이름으로 찾아 프로필 ID를 채운다.
 *
 * 숫자를 직접 넣게 하면 배우 목록 화면과 오가야 하고, 무엇보다 한 자리만 틀려도
 * 엉뚱한 배우에게 연결된다. 검색으로 고르게 하되 동명이인이 있을 수 있으므로
 * 출생연도·성별을 함께 보여준다.
 *
 * @param {number|string} value        선택된 talentProfileId
 * @param {string} [name]              이 항목에 적어둔 배우 이름 (선택 후 표시용)
 * @param {(id: number|null, actor: object|null) => void} onChange
 */
export default function TalentPicker({ value, name, onChange }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);

  // 늦게 도착한 예전 검색이 최신 결과를 덮어쓰지 않도록
  const reqIdRef = useRef(0);

  const search = useCallback(async (q) => {
    if (!q) { setResults([]); setError(null); return; }
    const myReq = ++reqIdRef.current;
    setSearching(true);
    setError(null);
    try {
      const { actors } = await searchAdminActors({ q, type: 'name', page: 1, limit: RESULT_LIMIT });
      if (myReq !== reqIdRef.current) return;
      setResults(actors);
    } catch (err) {
      if (myReq !== reqIdRef.current) return;
      setError(`배우를 찾지 못했습니다. (HTTP ${err.response?.status ?? '네트워크'})`);
      setResults([]);
    } finally {
      if (myReq === reqIdRef.current) setSearching(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => search(query.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query, search]);

  // 이미 고른 상태 — 무엇을 골랐는지 보이고 바꿀 수 있게 한다
  if (value !== '' && value != null) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '6px 10px', borderRadius: 8,
          border: '1px solid var(--color-border)', fontSize: 12,
        }}>
          <i className="ti ti-user-check" style={{ color: 'var(--color-success, #1D9E75)' }} />
          <span>{name || '연결됨'}</span>
          <span style={{ color: 'var(--color-text-tertiary)' }}>ID {value}</span>
        </span>
        <button
          type="button"
          className="ag-btn ag-btn--sm"
          onClick={() => { onChange(null, null); setQuery(''); setResults([]); setOpen(true); }}
        >
          연결 해제
        </button>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative' }}>
      <input
        className="ag-field__input"
        value={query}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder="가입 배우 이름으로 검색 (선택)"
        aria-label="가입 배우 검색"
        style={{ width: '100%' }}
      />

      {open && query.trim() && (
        <div
          style={{
            position: 'absolute', zIndex: 20, top: '100%', left: 0, right: 0, marginTop: 4,
            background: 'var(--color-surface, #fff)', border: '1px solid var(--color-border)',
            borderRadius: 8, boxShadow: '0 6px 18px rgba(0,0,0,0.12)',
            maxHeight: 240, overflowY: 'auto',
          }}
        >
          {searching ? (
            <div style={{ padding: '10px 12px', fontSize: 12, color: 'var(--color-text-tertiary)' }}>
              찾는 중...
            </div>
          ) : error ? (
            <div style={{ padding: '10px 12px', fontSize: 12, color: 'var(--color-danger)' }}>{error}</div>
          ) : results.length === 0 ? (
            <div style={{ padding: '10px 12px', fontSize: 12, color: 'var(--color-text-tertiary)' }}>
              검색 결과가 없습니다. 가입하지 않은 배우라면 비워두세요.
            </div>
          ) : (
            results.map(actor => (
              <button
                key={actor.id}
                type="button"
                onClick={() => { onChange(actor.id, actor); setOpen(false); setQuery(''); }}
                style={{
                  display: 'flex', alignItems: 'baseline', gap: 8, width: '100%',
                  padding: '8px 12px', border: 'none', background: 'transparent',
                  cursor: 'pointer', textAlign: 'left',
                }}
              >
                <span style={{ fontSize: 13, fontWeight: 500 }}>{actor.name}</span>
                <span style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>
                  {describeActor(actor)}
                </span>
                <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--color-text-tertiary)' }}>
                  ID {actor.id}
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
